import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, QueryList,
  SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { AgmInfoWindow, AgmMap, AgmMarker, LatLngBounds } from '@agm/core';
import { AgmSnazzyInfoWindow } from '@agm/snazzy-info-window';
import { ControlPosition, GoogleMap, MapTypeControlOptions } from '@agm/core/services/google-maps-types';

import { GoogleService } from '../../google/google.service';
import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { BoundingBox } from '../../util/geodesy/bounding-box';
import { LatLon } from '../../util/geodesy/latlon';
import { AviationEvent, AviationEventType } from '../../shared/models/aviation-event.model';
import { FlyingClubResult } from '../../shared/models/flying-club-result.model';
import { FlightSchoolResult } from '../../shared/models/flight-school-result.model';
import { UniversityResult } from '../../shared/models/university-result.model';
import { FboResult } from '../../shared/models/fbo-result.model';
import { ServiceRepairResult } from '../../shared/models/service-repair-result.model';
import { RentalSalesResult } from '../../shared/models/rental-sales-result.model';
import PlaceResult = google.maps.places.PlaceResult;
import { AviationEventFilter } from '../../shared/models/aviation-event-filter.model';
import * as moment from 'moment-timezone';
import { AnalyticsService } from '../../airport/service/google-analytics-4.service';
import { AuthService } from '../../auth/auth/auth.service';

// The business-finder markers (Flying Clubs or Flight Schools — only one tab is ever active) and their
// clustering are self-managed (see recluster()): plain google.maps.Marker bubbles that remove reliably via
// setMap(null). The old js-marker-clusterer leaked orphaned OverlayView cluster-icon DOM that nothing could
// clear (ghost "3" bubble showing for a 1-club search), so it was abandoned.

/**
 * Inline SVG data URI for a blue cluster bubble circle of the given px diameter (no external image needed).
 * base64-encoded so it is safe as a marker icon url. The count is drawn via the marker's label, not the SVG.
 */
function blueClusterBubble(size: number): string {
  const c = size / 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
      `<circle cx="${c}" cy="${c}" r="${c}" fill="#1e88e5" fill-opacity="0.3"/>` +
      `<circle cx="${c}" cy="${c}" r="${size * 0.36}" fill="#1e88e5"/>` +
    `</svg>`;
  return 'data:image/svg+xml;base64,' + btoa(svg);
}

/**
 * Inline SVG data URI for the flight-school map marker: a black (#212121) teardrop pin (evoking a graduation
 * mortar board) with a small white graduation cap. Kept inline (like {@link blueClusterBubble}) so no binary
 * asset is needed and the school pins read distinctly from the flying-club PNG pins.
 */
const FLIGHT_SCHOOL_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#212121"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path d="M13.5 8 4.5 11.5 13.5 15 22.5 11.5 13.5 8z" fill="#212121"/>' +
    '<path d="M8.5 13.2v3.1c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5v-3.1L13.5 15.4 8.5 13.2z" fill="#212121"/>' +
    '<rect x="21.7" y="11.5" width="1.1" height="4.2" fill="#212121"/>' +
  '</svg>');

/**
 * Inline SVG data URI for the university map marker: a purple (#6A1B9A) teardrop pin with a small white
 * classical building (pediment + columns), so university pins read distinctly from the flight-school pins.
 */
const UNIVERSITY_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#6A1B9A"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path d="M13.5 7.3 5 11.2 22 11.2z" fill="#6A1B9A"/>' +
    '<rect x="6.4" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="10" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="13.6" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="17.2" y="11.8" width="1.7" height="5" fill="#6A1B9A"/>' +
    '<rect x="5" y="17" width="16.5" height="1.7" fill="#6A1B9A"/>' +
  '</svg>');

/**
 * Inline SVG data URI for the Business Finder (FBO) map marker: a red (#D32F2F) teardrop pin with a small
 * white fuel pump, so FBO pins read distinctly from the other finders' pins.
 */
const FBO_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#D32F2F"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<rect x="8" y="7.6" width="6.4" height="11.8" rx="1.2" fill="#D32F2F"/>' +
    '<rect x="9.3" y="9" width="3.8" height="3" rx="0.4" fill="#ffffff"/>' +
    '<rect x="9.3" y="13.4" width="3.8" height="1.1" fill="#ffffff"/>' +
    '<rect x="16" y="11.2" width="1.4" height="8.2" rx="0.6" fill="#D32F2F"/>' +
    '<rect x="15.2" y="10" width="2.9" height="1.5" rx="0.6" fill="#D32F2F"/>' +
  '</svg>');

/**
 * Inline SVG data URI for the Service/Repair Finder map marker: a bright orange (#FF6D00) teardrop pin with a small
 * white circle carrying a green open-end wrench (the standard spanner glyph, matching the "build" tab icon),
 * so service/repair pins read distinctly from the other finders' pins. The wrench path is the 24x24 Material
 * "wrench" glyph, scaled/translated to sit inside the white circle.
 */
const SERVICE_REPAIR_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#FF6D00"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path transform="translate(5.9 5.9) scale(0.64)" fill="#FF6D00" ' +
      'd="M22.7 19L13.6 9.9C14.5 7.6 14 4.9 12.1 3C10.1 1 7.1 0.6 4.7 1.7L9 6L6 9L1.6 4.7C0.6 7.1 1 10.1 3 12.1C4.9 14 7.6 14.5 9.9 13.6L19 22.7C19.4 23.1 20 23.1 20.4 22.7L22.6 20.5C23.1 20 23.1 19.4 22.7 19Z"/>' +
  '</svg>');

/**
 * Inline SVG data URI for the Rentals/Sales Finder map marker: a deep green (#2E7D32) teardrop pin with a
 * small white circle carrying a green price tag (the Material "local_offer" glyph, matching the tab icon,
 * scaled/translated to sit inside the white circle), so rentals/sales pins read distinctly from the other
 * finders' pins.
 */
const RENTAL_SALES_MARKER_ICON = 'data:image/svg+xml;base64,' + btoa(
  '<svg xmlns="http://www.w3.org/2000/svg" width="27" height="40" viewBox="0 0 27 40">' +
    '<path d="M13.5 0C6 0 0 6 0 13.5 0 23.6 13.5 40 13.5 40S27 23.6 27 13.5C27 6 21 0 13.5 0z" fill="#2E7D32"/>' +
    '<circle cx="13.5" cy="13.5" r="9.5" fill="#ffffff"/>' +
    '<path transform="translate(5.9 5.9) scale(0.64)" fill="#2E7D32" ' +
      'd="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>' +
  '</svg>');


@Component({
  selector: 'app-results-map',
  templateUrl: './results-map.component.html',
  styleUrls: ['./results-map.component.scss']
})
export class ResultsMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() airports: Array<AirportSearchResult>;
  @Input() activities: Array<PlaceResult>;
  @Input() aviationEvents: Array<AviationEvent>;
  @Input() activeAirport: AirportSearchResult;     // Airport being hovered (small tooltip)
  @Input() selectedAirport: AirportSearchResult;   // Airport that was clicked (full size card)
  @Input() selectedActivity: PlaceResult;
  @Input() activeAviationEvent: AviationEvent;     // event being hovered (small tooltip)
  @Input() selectedAviationEvent: AviationEvent;   // event that was clicked (full size card)
  @Input() initialBounds: BoundingBox;
  @Input() message;
  @Input() showAirports: boolean;
  @Input() aviationEventsFilter: AviationEventFilter;
  @Input() onEventsTab = false;
  @Input() initialLocation: LatLon;
  @Input() flyingClubs: Array<FlyingClubResult> = [];
  @Input() activeFlyingClub: FlyingClubResult;       // club being hovered (small tooltip)
  @Input() onFlyingClubsTab = false;
  mapFlyingClubs: Array<FlyingClubResult> = [];      // clubs with coordinates, used for map markers
  @Input() flightSchools: Array<FlightSchoolResult> = [];
  @Input() activeFlightSchool: FlightSchoolResult;   // school being hovered (small tooltip)
  @Input() onFlightSchoolsTab = false;
  mapFlightSchools: Array<FlightSchoolResult> = [];  // schools with coordinates, used for map markers
  @Input() universities: Array<UniversityResult> = [];
  @Input() activeUniversity: UniversityResult;       // university being hovered (small tooltip)
  @Input() onUniversitiesTab = false;
  mapUniversities: Array<UniversityResult> = [];     // universities with coordinates, used for map markers
  @Input() fbos: Array<FboResult> = [];
  @Input() activeFbo: FboResult;                     // FBO being hovered (small tooltip)
  @Input() onFbosTab = false;
  mapFbos: Array<FboResult> = [];                    // FBOs with coordinates, used for map markers
  @Input() serviceRepairs: Array<ServiceRepairResult> = [];
  @Input() activeServiceRepair: ServiceRepairResult; // service/repair business being hovered (small tooltip)
  @Input() onServiceRepairsTab = false;
  mapServiceRepairs: Array<ServiceRepairResult> = []; // service/repair businesses with coordinates, used for map markers
  @Input() rentalSales: Array<RentalSalesResult> = [];
  @Input() activeRentalSales: RentalSalesResult;     // rentals/sales business being hovered (small tooltip)
  @Input() onRentalSalesTab = false;
  mapRentalSales: Array<RentalSalesResult> = [];     // rentals/sales businesses with coordinates, used for map markers
  currZoom = 0;                                      // map zoom, bound to <agm-map [zoom]> (mirrors events cluster)
  readonly CLUSTER_GRID_PX = 60;                     // markers within this pixel distance group into one bubble
  readonly DECLUSTER_ZOOM = 15;                      // at/above this zoom every club shows individually (no bubbles)
  @Output() airportClick = new EventEmitter<AirportSearchResult>();
  @Output() airportMouseEnter = new EventEmitter<AirportSearchResult>();
  @Output() airportMouseLeave = new EventEmitter<AirportSearchResult>();
  @Output() airportCardClose = new EventEmitter<void>();
  @Output() activityClick = new EventEmitter<PlaceResult>();
  @Output() activityMouseEnter = new EventEmitter<PlaceResult>();
  @Output() activityMouseLeave = new EventEmitter<PlaceResult>();
  @Output() activityCardClose = new EventEmitter<PlaceResult>();
  @Output() boundsChange = new EventEmitter<BoundingBox>();
  @Output() aviationEventClick = new EventEmitter<AviationEvent>();
  @Output() aviationEventMouseEnter = new EventEmitter<AviationEvent>();
  @Output() aviationEventMouseLeave = new EventEmitter<AviationEvent>();
  @Output() aviationEventCardClose = new EventEmitter<void>();
  @Output() flyingClubClick = new EventEmitter<FlyingClubResult>();
  @Output() flyingClubMouseEnter = new EventEmitter<FlyingClubResult>();
  @Output() flyingClubMouseLeave = new EventEmitter<FlyingClubResult>();
  @Output() flightSchoolClick = new EventEmitter<FlightSchoolResult>();
  @Output() flightSchoolMouseEnter = new EventEmitter<FlightSchoolResult>();
  @Output() flightSchoolMouseLeave = new EventEmitter<FlightSchoolResult>();
  @Output() universityClick = new EventEmitter<UniversityResult>();
  @Output() universityMouseEnter = new EventEmitter<UniversityResult>();
  @Output() universityMouseLeave = new EventEmitter<UniversityResult>();
  @Output() fboClick = new EventEmitter<FboResult>();
  @Output() fboMouseEnter = new EventEmitter<FboResult>();
  @Output() fboMouseLeave = new EventEmitter<FboResult>();
  @Output() serviceRepairClick = new EventEmitter<ServiceRepairResult>();
  @Output() serviceRepairMouseEnter = new EventEmitter<ServiceRepairResult>();
  @Output() serviceRepairMouseLeave = new EventEmitter<ServiceRepairResult>();
  @Output() rentalSalesClick = new EventEmitter<RentalSalesResult>();
  @Output() rentalSalesMouseEnter = new EventEmitter<RentalSalesResult>();
  @Output() rentalSalesMouseLeave = new EventEmitter<RentalSalesResult>();

  @ViewChild('map') map: AgmMap;
  @ViewChild('map', { read: ElementRef }) mapEl: ElementRef;
  @ViewChildren('airportMarker', { read: AgmMarker }) airportMarkers: QueryList<AgmMarker>;
  @ViewChildren('activityMarker', { read: AgmMarker }) activityMarkers: QueryList<AgmMarker>;
  @ViewChildren('aviationEventMarker', { read: AgmMarker }) aviationEventMarkers: QueryList<AgmMarker>;
  @ViewChild('airportInfoTooltip', { read: AgmInfoWindow }) airportInfoTooltip: AgmInfoWindow;
  @ViewChild('aviationEventInfoTooltip', { read: AgmInfoWindow }) aviationEventInfoTooltip: AgmInfoWindow;
  @ViewChildren('airportInfoWindow', { read: AgmInfoWindow }) airportInfoWindows: QueryList<AgmInfoWindow>;
  @ViewChildren('aviationEventInfoWindow', { read: AgmInfoWindow }) aviationEventInfoWindows: QueryList<AgmInfoWindow>;
  @ViewChildren('activityInfoWindow', { read: AgmSnazzyInfoWindow }) activityInfoWindows: QueryList<AgmSnazzyInfoWindow>;

  mapTypeControlOptions: MapTypeControlOptions = {
    position: ControlPosition.TOP_RIGHT
  };
  width: number;
  height: number;
  private _nativeMap: GoogleMap;
  private _clubMarkers: google.maps.Marker[] = [];    // one native marker per plottable club (shown when unclustered)
  private _schoolMarkers: google.maps.Marker[] = [];  // one native marker per plottable flight school
  private _universityMarkers: google.maps.Marker[] = []; // one native marker per plottable university
  private _fboMarkers: google.maps.Marker[] = [];     // one native marker per plottable FBO
  private _serviceRepairMarkers: google.maps.Marker[] = []; // one native marker per plottable service/repair business
  private _rentalSalesMarkers: google.maps.Marker[] = []; // one native marker per plottable rentals/sales business
  private _clusterBubbles: google.maps.Marker[] = [];  // count bubbles for the current zoom (rebuilt each re-cluster)
  private _finderInfoWindow: google.maps.InfoWindow;  // native hover tooltip shared by all finders (avoids AGM's manager mismatch on clustered markers)
  private _lastClusterZoom: number;                   // last zoom we re-clustered at
  private _reclusterTimer: any;                        // debounce for the re-cluster
  hoverTimer: any;

  /**
   * The active business-finder marker set to cluster, by tab: universities / flight schools / (default)
   * flying clubs. Only one finder tab is ever active, so the inactive sets are empty and this is unambiguous.
   */
  private get _finderMarkers(): google.maps.Marker[] {
    if (this.onFlightSchoolsTab) {
      return this._schoolMarkers;
    }
    if (this.onUniversitiesTab) {
      return this._universityMarkers;
    }
    if (this.onFbosTab) {
      return this._fboMarkers;
    }
    if (this.onServiceRepairsTab) {
      return this._serviceRepairMarkers;
    }
    if (this.onRentalSalesTab) {
      return this._rentalSalesMarkers;
    }
    return this._clubMarkers;
  }

  constructor(
    private _zone: NgZone,
    private el: ElementRef,
    private _google: GoogleService,
    private _analyticsService: AnalyticsService,
    private _auth: AuthService
  ) {}

  get identifier(): string {
    if (this.activeAirport.identifier != null && this.activeAirport.identifier.toUpperCase().startsWith('ZZ')) {
      return '';
    } else {
      return this.activeAirport.identifier + ' :';
    }
  }

  get eventDatesMessage(): string {
    const queryStart = moment(new Date(this.aviationEventsFilter.startDate).setHours(0, 0, 0, 0));
    const queryEnd = moment(new Date(this.aviationEventsFilter.endDate).setHours(23, 59, 59, 999));
    const queryStartDateDisplay = moment(queryStart).local().format('MM-DD-YYYY');
    const queryEndDateDisplay = moment(queryEnd).local().format('MM-DD-YYYY');
    return 'Displaying Events From ' + queryStartDateDisplay + ' TO ' + queryEndDateDisplay;
  }

  async ngAfterViewInit() {
    this.map.mapReady.subscribe(map => {
      this._nativeMap = map;
      this._finderInfoWindow = new google.maps.InfoWindow({ disableAutoPan: true });
      // Re-cluster when the map settles at a new zoom. This raw 'idle' can be unreliable on this map, so
      // onZoomChange() (AGM's (zoomChange), which DOES fire) also drives scheduleRecluster(); idle also
      // handles the first clustering once the projection is ready. Both share the debounced rebuild.
      google.maps.event.addListener(map as any, 'idle', () => {
        const zoom = (map as any).getZoom();
        if (zoom !== this._lastClusterZoom) {
          this._lastClusterZoom = zoom;
          this.scheduleRecluster();
        }
      });
      this.renderFlyingClubMarkers();
      this.renderFlightSchoolMarkers();
      this.renderUniversityMarkers();
      this.renderFboMarkers();
      this.renderServiceRepairMarkers();
      this.renderRentalSalesMarkers();
    });

    await this._google.promise;

    // Initialize map bounds
    this.updateBounds();
    this._analyticsService.pageView('airport_search_results', 'Airport/Event Search Results');
    if (this._auth.authenticated && this._auth.endUser) {
      this._analyticsService.trackEvent('airport_search_results_loggedin', this._auth.recid, this._auth.recid);
    } else {
      this._analyticsService.trackEvent('airport_search_results_loggedout', '', '');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.aviationEvents = this.aviationEvents.filter((ae) => {
      if ((ae.format === 'In-Person' || ae.format === 'Both') &&
        (ae.location && ae.location.lat && ae.location.lon)) {
        return true;
      } else {
        return false;
      }
    });

    // Rebuild club markers only when the club set or tab actually changed — not on every input change
    // (e.g. activeFlyingClub on list hover) — so we don't churn/rebuild markers underneath the clusterer.
    if (changes['flyingClubs'] || changes['onFlyingClubsTab']) {
      // Only clubs with coordinates can be plotted; keep indices aligned with the tooltip lookup. Off-tab = none.
      this.mapFlyingClubs = this.onFlyingClubsTab
        ? (this.flyingClubs || []).filter(fc => fc.location && fc.location.lat && fc.location.lon)
        : [];
      this.renderFlyingClubMarkers();
    }

    // Same as above for flight schools (only one finder tab is active, so the other set ends up empty).
    if (changes['flightSchools'] || changes['onFlightSchoolsTab']) {
      this.mapFlightSchools = this.onFlightSchoolsTab
        ? (this.flightSchools || []).filter(fs => fs.location && fs.location.lat && fs.location.lon)
        : [];
      this.renderFlightSchoolMarkers();
    }

    // Same as above for universities.
    if (changes['universities'] || changes['onUniversitiesTab']) {
      this.mapUniversities = this.onUniversitiesTab
        ? (this.universities || []).filter(u => u.location && u.location.lat && u.location.lon)
        : [];
      this.renderUniversityMarkers();
    }

    // Same as above for FBOs (Business Finder).
    if (changes['fbos'] || changes['onFbosTab']) {
      this.mapFbos = this.onFbosTab
        ? (this.fbos || []).filter(fbo => fbo.location && fbo.location.lat && fbo.location.lon)
        : [];
      this.renderFboMarkers();
    }

    // Same as above for the Service/Repair Finder.
    if (changes['serviceRepairs'] || changes['onServiceRepairsTab']) {
      this.mapServiceRepairs = this.onServiceRepairsTab
        ? (this.serviceRepairs || []).filter(sr => sr.location && sr.location.lat && sr.location.lon)
        : [];
      this.renderServiceRepairMarkers();
    }

    // Same as above for the Rentals/Sales Finder.
    if (changes['rentalSales'] || changes['onRentalSalesTab']) {
      this.mapRentalSales = this.onRentalSalesTab
        ? (this.rentalSales || []).filter(rs => rs.location && rs.location.lat && rs.location.lon)
        : [];
      this.renderRentalSalesMarkers();
    }
  }

  /** Rebuilds the per-club native markers (kept off-map until placed by recluster) then re-clusters them. */
  renderFlyingClubMarkers() {
    if (!this._nativeMap) {
      return;   // map not ready yet — ngAfterViewInit's mapReady will call this again
    }
    // Drop the previous markers + bubbles and their listeners.
    this._clubMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    this._clubMarkers = this.mapFlyingClubs.map(club => {
      const marker = new google.maps.Marker({
        position: { lat: club.location.lat, lng: club.location.lon },
        icon: this.getFlyingClubIconUrl(club),
        opacity: 0.8,
        title: club.name
      });
      marker.addListener('click', () => this._zone.run(() => this.onFlyingClubMarkerClick(club)));
      marker.addListener('mouseover', () => this._zone.run(() => this.onFlyingClubMarkerHover(club, true)));
      marker.addListener('mouseout', () => this._zone.run(() => this.onFlyingClubMarkerHover(club, false)));
      return marker;
    });

    this.recluster();
  }

  /** Rebuilds the per-school native markers (kept off-map until placed by recluster) then re-clusters them. */
  renderFlightSchoolMarkers() {
    if (!this._nativeMap) {
      return;   // map not ready yet — ngAfterViewInit's mapReady will call this again
    }
    // Drop the previous markers + bubbles and their listeners.
    this._schoolMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    this._schoolMarkers = this.mapFlightSchools.map(school => {
      const marker = new google.maps.Marker({
        position: { lat: school.location.lat, lng: school.location.lon },
        icon: this.getFlightSchoolIconUrl(school),
        opacity: 0.8,
        title: school.name
      });
      marker.addListener('click', () => this._zone.run(() => this.onFlightSchoolMarkerClick(school)));
      marker.addListener('mouseover', () => this._zone.run(() => this.onFlightSchoolMarkerHover(school, true)));
      marker.addListener('mouseout', () => this._zone.run(() => this.onFlightSchoolMarkerHover(school, false)));
      return marker;
    });

    this.recluster();
  }

  /** Rebuilds the per-university native markers (kept off-map until placed by recluster) then re-clusters them. */
  renderUniversityMarkers() {
    if (!this._nativeMap) {
      return;   // map not ready yet — ngAfterViewInit's mapReady will call this again
    }
    // Drop the previous markers + bubbles and their listeners.
    this._universityMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    this._universityMarkers = this.mapUniversities.map(university => {
      const marker = new google.maps.Marker({
        position: { lat: university.location.lat, lng: university.location.lon },
        icon: this.getUniversityIconUrl(university),
        opacity: 0.8,
        title: university.name
      });
      marker.addListener('click', () => this._zone.run(() => this.onUniversityMarkerClick(university)));
      marker.addListener('mouseover', () => this._zone.run(() => this.onUniversityMarkerHover(university, true)));
      marker.addListener('mouseout', () => this._zone.run(() => this.onUniversityMarkerHover(university, false)));
      return marker;
    });

    this.recluster();
  }

  /** Rebuilds the per-FBO native markers (kept off-map until placed by recluster) then re-clusters them. */
  renderFboMarkers() {
    if (!this._nativeMap) {
      return;   // map not ready yet — ngAfterViewInit's mapReady will call this again
    }
    // Drop the previous markers + bubbles and their listeners.
    this._fboMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    this._fboMarkers = this.mapFbos.map(fbo => {
      const marker = new google.maps.Marker({
        position: { lat: fbo.location.lat, lng: fbo.location.lon },
        icon: this.getFboIconUrl(fbo),
        opacity: 0.8,
        title: fbo.name
      });
      marker.addListener('click', () => this._zone.run(() => this.onFboMarkerClick(fbo)));
      marker.addListener('mouseover', () => this._zone.run(() => this.onFboMarkerHover(fbo, true)));
      marker.addListener('mouseout', () => this._zone.run(() => this.onFboMarkerHover(fbo, false)));
      return marker;
    });

    this.recluster();
  }

  /** Rebuilds the per-business Service/Repair native markers (kept off-map until placed by recluster) then re-clusters them. */
  renderServiceRepairMarkers() {
    if (!this._nativeMap) {
      return;   // map not ready yet — ngAfterViewInit's mapReady will call this again
    }
    // Drop the previous markers + bubbles and their listeners.
    this._serviceRepairMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    this._serviceRepairMarkers = this.mapServiceRepairs.map(sr => {
      const marker = new google.maps.Marker({
        position: { lat: sr.location.lat, lng: sr.location.lon },
        icon: this.getServiceRepairIconUrl(sr),
        opacity: 0.8,
        title: sr.name
      });
      marker.addListener('click', () => this._zone.run(() => this.onServiceRepairMarkerClick(sr)));
      marker.addListener('mouseover', () => this._zone.run(() => this.onServiceRepairMarkerHover(sr, true)));
      marker.addListener('mouseout', () => this._zone.run(() => this.onServiceRepairMarkerHover(sr, false)));
      return marker;
    });

    this.recluster();
  }

  /** Rebuilds the per-business Rentals/Sales native markers (kept off-map until placed by recluster) then re-clusters them. */
  renderRentalSalesMarkers() {
    if (!this._nativeMap) {
      return;   // map not ready yet — ngAfterViewInit's mapReady will call this again
    }
    // Drop the previous markers + bubbles and their listeners.
    this._rentalSalesMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    this._rentalSalesMarkers = this.mapRentalSales.map(rs => {
      const marker = new google.maps.Marker({
        position: { lat: rs.location.lat, lng: rs.location.lon },
        icon: this.getRentalSalesIconUrl(rs),
        opacity: 0.8,
        title: rs.name
      });
      marker.addListener('click', () => this._zone.run(() => this.onRentalSalesMarkerClick(rs)));
      marker.addListener('mouseover', () => this._zone.run(() => this.onRentalSalesMarkerHover(rs, true)));
      marker.addListener('mouseout', () => this._zone.run(() => this.onRentalSalesMarkerHover(rs, false)));
      return marker;
    });

    this.recluster();
  }

  ngOnDestroy() {
    clearTimeout(this._reclusterTimer);
    this._clubMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._schoolMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._universityMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._fboMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._serviceRepairMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._rentalSalesMarkers.forEach(m => { google.maps.event.clearInstanceListeners(m); m.setMap(null); });
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clubMarkers = [];
    this._schoolMarkers = [];
    this._universityMarkers = [];
    this._fboMarkers = [];
    this._serviceRepairMarkers = [];
    this._rentalSalesMarkers = [];
    this._clusterBubbles = [];
  }

  /** Keeps the component's zoom in sync with the map, and drives the re-cluster (raw 'idle' is unreliable here). */
  onZoomChange(newZoomValue: number) {
    this.currZoom = newZoomValue;
    this._lastClusterZoom = newZoomValue;
    this.scheduleRecluster();
  }

  /** Debounced re-cluster (coalesces rapid zoom settles). */
  scheduleRecluster() {
    if (!this._nativeMap || !this._finderMarkers.length) {
      return;
    }
    clearTimeout(this._reclusterTimer);
    this._reclusterTimer = setTimeout(() => this.recluster(), 120);
  }

  /**
   * Self-managed grid clustering. Groups club markers within CLUSTER_GRID_PX screen pixels (at the current
   * zoom) into one count bubble and shows the rest individually. At/above DECLUSTER_ZOOM (or before the map
   * projection is ready) every club shows individually. Bubbles are plain google.maps.Markers, so setMap(null)
   * reliably removes them — no orphaned overlay DOM (the whole reason the js-marker-clusterer was dropped).
   */
  recluster() {
    const markers = this._finderMarkers;
    if (!this._nativeMap || !markers.length) {
      return;
    }
    // Remove the previous bubbles (reliable — plain markers).
    this._clusterBubbles.forEach(b => { google.maps.event.clearInstanceListeners(b); b.setMap(null); });
    this._clusterBubbles = [];

    const map: any = this._nativeMap;
    const zoom: number = map.getZoom();
    const projection = map.getProjection();

    // No clustering: show every marker individually.
    if (!projection || zoom >= this.DECLUSTER_ZOOM) {
      markers.forEach(m => { if (m.getMap() !== map) { m.setMap(map); } });
      return;
    }

    // Project each marker to screen pixels (world point * 2^zoom), then greedily group by pixel proximity.
    const scale = Math.pow(2, zoom);
    const pts = markers.map(m => {
      const wp = projection.fromLatLngToPoint(m.getPosition());
      return { x: wp.x * scale, y: wp.y * scale };
    });

    const used = markers.map(() => false);
    for (let i = 0; i < markers.length; i++) {
      if (used[i]) { continue; }
      const group = [i];
      used[i] = true;
      for (let j = i + 1; j < markers.length; j++) {
        if (used[j]) { continue; }
        const dx = pts[i].x - pts[j].x;
        const dy = pts[i].y - pts[j].y;
        if (Math.sqrt(dx * dx + dy * dy) <= this.CLUSTER_GRID_PX) {
          group.push(j);
          used[j] = true;
        }
      }

      if (group.length === 1) {
        const marker = markers[group[0]];
        if (marker.getMap() !== map) { marker.setMap(map); }
      } else {
        let lat = 0, lng = 0;
        group.forEach(k => {
          const pos = markers[k].getPosition();
          lat += pos.lat();
          lng += pos.lng();
          markers[k].setMap(null);   // hide members behind the bubble
        });
        this._clusterBubbles.push(this.makeClusterBubble({ lat: lat / group.length, lng: lng / group.length }, group.length));
      }
    }
  }

  /** Builds a plain-marker count bubble (blue circle icon + white count label). */
  private makeClusterBubble(position: google.maps.LatLngLiteral, count: number): google.maps.Marker {
    const size = count < 10 ? 38 : (count < 100 ? 46 : 54);
    const bubble = new google.maps.Marker({
      position,
      map: this._nativeMap as any,
      icon: {
        url: blueClusterBubble(size),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
        labelOrigin: new google.maps.Point(size / 2, size / 2)
      },
      label: { text: String(count), color: '#ffffff', fontWeight: 'bold', fontSize: count < 100 ? '13px' : '11px' },
      zIndex: 10000
    });
    bubble.addListener('click', () => this._zone.run(() => this.onClusterBubbleClick(position)));
    return bubble;
  }

  /** Zoom toward a cluster when its bubble is clicked. */
  private onClusterBubbleClick(position: google.maps.LatLngLiteral) {
    const map: any = this._nativeMap;
    map.panTo(position);
    map.setZoom(Math.min(map.getZoom() + 3, this.DECLUSTER_ZOOM));
  }

  updateSize() {
    this.map.triggerResize(true);
  }

  setCenter(center: LatLon) {
    const latlon = { lat: center.lat, lng: center.lon };
    this._nativeMap.setCenter(latlon);
  }

  updateBounds() {
    setTimeout(async () => {
      await this._google.promise;
      let bounds: google.maps.LatLngBounds|google.maps.LatLngBoundsLiteral;
      if ((this.airports || []).length > 0 || (this.activities || []).length || (this.flyingClubs || []).length
          || (this.flightSchools || []).length || (this.universities || []).length
          || (this.fbos || []).length || (this.serviceRepairs || []).length
          || (this.rentalSales || []).length) {  //  || (this.aviationEvents || []).length
        // Fit airport markers
        bounds = new google.maps.LatLngBounds();
        (this.airports || []).forEach(airport => {
          (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(airport.location.lat, airport.location.lon));
        });

        // Fit activity markers
        (this.activities || []).forEach(activity => {
          (<google.maps.LatLngBounds>bounds).extend(activity.geometry.location);
        });

        // Fit flying club markers
        (this.flyingClubs || []).forEach(flyingClub => {
          if (flyingClub.location && flyingClub.location.lat && flyingClub.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(flyingClub.location.lat, flyingClub.location.lon));
          }
        });

        // Fit flight school markers
        (this.flightSchools || []).forEach(flightSchool => {
          if (flightSchool.location && flightSchool.location.lat && flightSchool.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(flightSchool.location.lat, flightSchool.location.lon));
          }
        });

        // Fit university markers
        (this.universities || []).forEach(university => {
          if (university.location && university.location.lat && university.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(university.location.lat, university.location.lon));
          }
        });

        // Fit FBO markers
        (this.fbos || []).forEach(fbo => {
          if (fbo.location && fbo.location.lat && fbo.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(fbo.location.lat, fbo.location.lon));
          }
        });

        // Fit service/repair markers
        (this.serviceRepairs || []).forEach(sr => {
          if (sr.location && sr.location.lat && sr.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(sr.location.lat, sr.location.lon));
          }
        });

        // Fit rentals/sales markers
        (this.rentalSales || []).forEach(rs => {
          if (rs.location && rs.location.lat && rs.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(rs.location.lat, rs.location.lon));
          }
        });

        /* (this.aviationEvents || []).forEach(aviationEvent => {
          if (aviationEvent.location && aviationEvent.location.lat && aviationEvent.location.lon) {
            (<google.maps.LatLngBounds>bounds).extend(new google.maps.LatLng(aviationEvent.location.lat, aviationEvent.location.lon));
          }
        }); */

        // Add an offset around the center so as to establish a maximum zoom
        const offset = 0.008;
        const center = bounds.getCenter();
        bounds.extend(new google.maps.LatLng(center.lat() + offset, center.lng() + offset));
        bounds.extend(new google.maps.LatLng(center.lat() - offset, center.lng() - offset));
      } else if (this.initialBounds) {
        bounds = {
          north: this.initialBounds.ne.lat,
          east: this.initialBounds.ne.lon,
          south: this.initialBounds.sw.lat,
          west: this.initialBounds.sw.lon
        };
      } else if (this.initialLocation) {
        bounds = new google.maps.LatLngBounds();
        const offset = 2.5;
        bounds.extend(new google.maps.LatLng(this.initialLocation.lat + offset, this.initialLocation.lon + offset));
        bounds.extend(new google.maps.LatLng(this.initialLocation.lat - offset, this.initialLocation.lon - offset));
        this.setCenter(this.initialLocation);
      } else {
        // Center on CONUS
        bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(24.396308, -124.848974),
          new google.maps.LatLng(49.384358, -66.885444),
        );
      }
      this._nativeMap.fitBounds(bounds as any);
    }, 0);
  }

  getIconUrl(activity: PlaceResult): string {
    return GoogleService.getIconUrl(activity);
  }

  getAirportIconUrl(airport: AirportSearchResult): string {
    if (!airport.minPrice || !airport.minPriceType) {
      return 'assets/icons/airport-map-pin.png';
    }

    if (airport.minPriceType === 'High') {
      return 'assets/icons/square-pin-red.png';
    } else if (airport.minPriceType === 'Medium') {
      return 'assets/icons/square-pin-yellow.png';
    } else {
      return 'assets/icons/square-pin-green.png';
    }
  }

  getAviationEventIconUrl(aviationEvent: AviationEvent): string {
    if (aviationEvent.type === AviationEventType.RustyPilotsSeminar) {
      return 'assets/icons/Rusty-Pilots-Event-Map-Marker.png';
    } else {
      return 'assets/icons/Event-Map-Marker.png';
    }
  }

  getEventIconZIndex(aviationEvent: AviationEvent): number {
    if (aviationEvent.type === AviationEventType.RustyPilotsSeminar) {
      return 600;
    } else {
      return 500;
    }
  }

  getFlyingClubIconUrl(flyingClub: FlyingClubResult): string {
    // Marker icon by club type, matching FlyingclubSummaryComponent.iconUrl.
    if (flyingClub.clubType === 'AOPA Network Club') {
      return 'assets/icons/networkicon.png';
    }
    if (flyingClub.clubType === 'Club In Formation') {
      return 'assets/icons/informationicon.png';
    }
    return 'assets/icons/othericon.png';
  }

  getFlightSchoolIconUrl(flightSchool: FlightSchoolResult): string {
    // All flight schools share the inline blue graduation-cap pin (distinct from the flying-club PNG pins).
    return FLIGHT_SCHOOL_MARKER_ICON;
  }

  getUniversityIconUrl(university: UniversityResult): string {
    // All universities share the inline purple building pin (distinct from the flight-school pins).
    return UNIVERSITY_MARKER_ICON;
  }

  getFboIconUrl(fbo: FboResult): string {
    // All FBOs share the inline red fuel-pump pin (distinct from the other finders' pins).
    return FBO_MARKER_ICON;
  }

  getServiceRepairIconUrl(serviceRepair: ServiceRepairResult): string {
    // All service/repair businesses share the inline green wrench pin (distinct from the other finders' pins).
    return SERVICE_REPAIR_MARKER_ICON;
  }

  getRentalSalesIconUrl(rentalSales: RentalSalesResult): string {
    // All rentals/sales businesses share the inline green price-tag pin (distinct from the other finders' pins).
    return RENTAL_SALES_MARKER_ICON;
  }

  onAirportMarkerHover (airport: AirportSearchResult, hover: boolean) {
    this.toggleAirportInfoTooltip(airport, hover);
    if (hover) {
      this.airportMouseEnter.emit(airport);
    } else {
      this.airportMouseLeave.emit(airport);
    }
  }

  onAirportMarkerClick (airport: AirportSearchResult) {
    this.airportClick.emit(airport);
  }

  onActivityMarkerHover (activity: PlaceResult, hover: boolean) {
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
    if (hover) {
      this.activityMouseEnter.emit(activity);
    } else {
        this.activityMouseLeave.emit(activity);
      }
    }, 400);
  }

  onActivityMarkerClick (activity: PlaceResult) {
    this.activityClick.emit(activity);
  }

  onAviationEventMarkerHover (aviationEvent: AviationEvent, hover: boolean) {
    this.toggleAviationEventInfoTooltip(aviationEvent, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.aviationEventMouseEnter.emit(aviationEvent);
      } else {
        this.aviationEventMouseLeave.emit(aviationEvent);
      }
    }, 400);
  }

  onAviationEventMarkerClick (aviationEvent: AviationEvent) {
    this.aviationEventClick.emit(aviationEvent);
  }

  onFlyingClubMarkerHover (flyingClub: FlyingClubResult, hover: boolean) {
    this.toggleFlyingClubInfoTooltip(flyingClub, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.flyingClubMouseEnter.emit(flyingClub);
      } else {
        this.flyingClubMouseLeave.emit(flyingClub);
      }
    }, 400);
  }

  onFlyingClubMarkerClick (flyingClub: FlyingClubResult) {
    this.flyingClubClick.emit(flyingClub);
  }

  onFlightSchoolMarkerHover (flightSchool: FlightSchoolResult, hover: boolean) {
    this.toggleFlightSchoolInfoTooltip(flightSchool, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.flightSchoolMouseEnter.emit(flightSchool);
      } else {
        this.flightSchoolMouseLeave.emit(flightSchool);
      }
    }, 400);
  }

  onFlightSchoolMarkerClick (flightSchool: FlightSchoolResult) {
    this.flightSchoolClick.emit(flightSchool);
  }

  onUniversityMarkerHover (university: UniversityResult, hover: boolean) {
    this.toggleUniversityInfoTooltip(university, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.universityMouseEnter.emit(university);
      } else {
        this.universityMouseLeave.emit(university);
      }
    }, 400);
  }

  onUniversityMarkerClick (university: UniversityResult) {
    this.universityClick.emit(university);
  }

  onFboMarkerHover (fbo: FboResult, hover: boolean) {
    this.toggleFboInfoTooltip(fbo, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.fboMouseEnter.emit(fbo);
      } else {
        this.fboMouseLeave.emit(fbo);
      }
    }, 400);
  }

  onFboMarkerClick (fbo: FboResult) {
    this.fboClick.emit(fbo);
  }

  onServiceRepairMarkerHover (serviceRepair: ServiceRepairResult, hover: boolean) {
    this.toggleServiceRepairInfoTooltip(serviceRepair, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.serviceRepairMouseEnter.emit(serviceRepair);
      } else {
        this.serviceRepairMouseLeave.emit(serviceRepair);
      }
    }, 400);
  }

  onServiceRepairMarkerClick (serviceRepair: ServiceRepairResult) {
    this.serviceRepairClick.emit(serviceRepair);
  }

  onRentalSalesMarkerHover (rentalSales: RentalSalesResult, hover: boolean) {
    this.toggleRentalSalesInfoTooltip(rentalSales, hover);
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      if (hover) {
        this.rentalSalesMouseEnter.emit(rentalSales);
      } else {
        this.rentalSalesMouseLeave.emit(rentalSales);
      }
    }, 400);
  }

  onRentalSalesMarkerClick (rentalSales: RentalSalesResult) {
    this.rentalSalesClick.emit(rentalSales);
  }

  toggleAirportInfoTooltip(airport: AirportSearchResult, open: boolean) {
    // Don't show tooltip window if the full size card is opened
    open = open && (this.selectedAirport !== airport);
    this.toggleAirportInfoWindow(airport, this.airportInfoTooltip, open);
  }

  toggleAirportInfoWindow(airport: AirportSearchResult, window: AgmInfoWindow, open: boolean) {
    const i = this.airports.indexOf(airport);
    if (i < 0) {
      return;
    }
    const marker = this.airportMarkers.toArray()[i];
    if (open) {
      this.infoWindowOpen(window, marker);
    } else {
      this.infoWindowClose(window);
    }
  }

  toggleAviationEventInfoTooltip(aviationEvent: AviationEvent, open: boolean) {
    // Don't show tooltip window if the full size card is opened
    open = open && (this.selectedAviationEvent !== aviationEvent);
    this.toggleAviationEventInfoWindow(aviationEvent, this.aviationEventInfoTooltip, open);
  }

  toggleAviationEventInfoWindow(aviationEvent: AviationEvent, window: AgmInfoWindow, open: boolean) {
    const i = this.aviationEvents.indexOf(aviationEvent);
    if (i < 0) {
      return;
    }
    const marker = this.aviationEventMarkers.toArray()[i];
    if (open) {
      this.infoWindowOpen(window, marker);
    } else {
      this.infoWindowClose(window);
    }
  }

  toggleFlyingClubInfoTooltip(flyingClub: FlyingClubResult, open: boolean) {
    if (!this._finderInfoWindow || !this._nativeMap) {
      return;
    }
    if (open) {
      const marker = this._clubMarkers[this.mapFlyingClubs.indexOf(flyingClub)];
      if (!marker) {
        return;
      }
      const content = document.createElement('div');
      content.className = 'results-map__airport-tooltip';
      content.textContent = flyingClub.name;
      this._finderInfoWindow.setContent(content);
      this._openFinderInfoWindow(marker);
    } else {
      this._zone.runOutsideAngular(() => this._finderInfoWindow.close());
    }
  }

  toggleFlightSchoolInfoTooltip(flightSchool: FlightSchoolResult, open: boolean) {
    if (!this._finderInfoWindow || !this._nativeMap) {
      return;
    }
    if (open) {
      const marker = this._schoolMarkers[this.mapFlightSchools.indexOf(flightSchool)];
      if (!marker) {
        return;
      }
      const content = document.createElement('div');
      content.className = 'results-map__airport-tooltip';
      content.textContent = flightSchool.name;
      this._finderInfoWindow.setContent(content);
      this._openFinderInfoWindow(marker);
    } else {
      this._zone.runOutsideAngular(() => this._finderInfoWindow.close());
    }
  }

  toggleUniversityInfoTooltip(university: UniversityResult, open: boolean) {
    if (!this._finderInfoWindow || !this._nativeMap) {
      return;
    }
    if (open) {
      const marker = this._universityMarkers[this.mapUniversities.indexOf(university)];
      if (!marker) {
        return;
      }
      const content = document.createElement('div');
      content.className = 'results-map__airport-tooltip';
      content.textContent = university.name;
      this._finderInfoWindow.setContent(content);
      this._openFinderInfoWindow(marker);
    } else {
      this._zone.runOutsideAngular(() => this._finderInfoWindow.close());
    }
  }

  toggleFboInfoTooltip(fbo: FboResult, open: boolean) {
    if (!this._finderInfoWindow || !this._nativeMap) {
      return;
    }
    if (open) {
      const marker = this._fboMarkers[this.mapFbos.indexOf(fbo)];
      if (!marker) {
        return;
      }
      const content = document.createElement('div');
      content.className = 'results-map__airport-tooltip';
      content.textContent = fbo.name;
      this._finderInfoWindow.setContent(content);
      this._openFinderInfoWindow(marker);
    } else {
      this._zone.runOutsideAngular(() => this._finderInfoWindow.close());
    }
  }

  toggleServiceRepairInfoTooltip(serviceRepair: ServiceRepairResult, open: boolean) {
    if (!this._finderInfoWindow || !this._nativeMap) {
      return;
    }
    if (open) {
      const marker = this._serviceRepairMarkers[this.mapServiceRepairs.indexOf(serviceRepair)];
      if (!marker) {
        return;
      }
      const content = document.createElement('div');
      content.className = 'results-map__airport-tooltip';
      content.textContent = serviceRepair.name;
      this._finderInfoWindow.setContent(content);
      this._openFinderInfoWindow(marker);
    } else {
      this._zone.runOutsideAngular(() => this._finderInfoWindow.close());
    }
  }

  toggleRentalSalesInfoTooltip(rentalSales: RentalSalesResult, open: boolean) {
    if (!this._finderInfoWindow || !this._nativeMap) {
      return;
    }
    if (open) {
      const marker = this._rentalSalesMarkers[this.mapRentalSales.indexOf(rentalSales)];
      if (!marker) {
        return;
      }
      const content = document.createElement('div');
      content.className = 'results-map__airport-tooltip';
      content.textContent = rentalSales.name;
      this._finderInfoWindow.setContent(content);
      this._openFinderInfoWindow(marker);
    } else {
      this._zone.runOutsideAngular(() => this._finderInfoWindow.close());
    }
  }

  /**
   * Opens the shared finder tooltip (content already set) for a marker. When the marker is visible it anchors
   * to it, so the bubble floats above the pin. When the marker is hidden inside a cluster it opens by explicit
   * position instead — otherwise Google Maps ignores the off-map anchor and leaves the window on the
   * previously hovered (visible) marker, making the tooltip appear over a random pin.
   */
  private _openFinderInfoWindow(marker: google.maps.Marker) {
    this._zone.runOutsideAngular(() => {
      if (marker.getMap()) {
        this._finderInfoWindow.open(this._nativeMap as any, marker);
      } else {
        this._finderInfoWindow.close();
        this._finderInfoWindow.setPosition(marker.getPosition());
        this._finderInfoWindow.open(this._nativeMap as any);
      }
    });
  }

  infoWindowOpen(infoWindow: AgmInfoWindow, marker: AgmMarker = null) {
    infoWindow.hostMarker = marker;
    // Run outside angular to avoid triggering an endless cycle of change detection:
    // https://github.com/SebastianM/angular-google-maps/issues/796
    this._zone.runOutsideAngular(() => {
      infoWindow.open();
    });
  }

  infoWindowClose(infoWindow: any) {
    this._zone.runOutsideAngular(() => {
      infoWindow.close();
    });
  }

  onBoundsChange(bounds: LatLngBounds) {
    const ne = LatLon.fromAgm(bounds.getNorthEast());
    const sw = LatLon.fromAgm(bounds.getSouthWest());
    const box = new BoundingBox(ne, sw);
    this.boundsChange.emit(box);
  }

  onCloseAirportCardWindow() {
    this.airportCardClose.emit();
  }

  onCloseActivityWindow(activity) {
    this.activityCardClose.emit(activity);
  }

  onCloseAviationEventCardWindow() {
    this.aviationEventCardClose.emit();
  }
}
